import {
  Box,
  Stack,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  UploadFileRounded,
  LinkRounded,
  PlaylistAddRounded,
  DeleteOutlineRounded,
  EditNoteRounded,
  OpenInNewRounded,
  VisibilityRounded,
  CloseRounded,
} from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenantStore } from '../../stores/tenant.store';
import {
  courseMaterialApi,
  type MaterialType,
  type SectionMaterialDto,
} from '../../lib/api/course-material.api';
import { SectionPicker } from './components/SectionPicker';
import { errorText } from '../../lib/error-text';
import {
  UploadMaterialDialog,
  LinkMaterialDialog,
  BulkLinkMaterialDialog,
  PreviewBody,
  TYPE_META,
} from './components/material-dialogs';

/**
 * The class-materials manager. Standalone it owns its own SectionPicker; when
 * `embeddedSectionId` is passed (the "Biblioteca" tab inside Curso/LMS) it hides
 * its header + picker and works against that section — one shared surface, no
 * duplicated dialogs.
 */
export function MaterialsAdminPage({
  embeddedSectionId,
  hideMaterialIds,
  moduleOptions,
  onAssignToModule,
}: {
  embeddedSectionId?: string;
  /** Material ids to hide (the ones already assigned to a module). The embedded
   *  Biblioteca is a pool of UNASSIGNED materials only. */
  hideMaterialIds?: string[];
  /** Modules of the section, to add an unassigned material into one. */
  moduleOptions?: { id: string; title: string }[];
  /** Add a material to a module (it then leaves the Biblioteca pool). */
  onAssignToModule?: (materialId: string, moduleId: string) => Promise<void>;
} = {}): JSX.Element {
  const { t } = useTranslation(['sis', 'common']);
  const { currentTenantId } = useTenantStore();

  const embedded = embeddedSectionId !== undefined;
  const showAssign = embedded && !!moduleOptions && !!onAssignToModule;
  const hidden = useMemo(() => new Set(hideMaterialIds ?? []), [hideMaterialIds]);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [selectedSectionId, setSelectedSectionId] = useState<string>('');
  const effectiveSectionId = embedded ? embeddedSectionId : selectedSectionId;
  const [materials, setMaterials] = useState<SectionMaterialDto[]>([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState<SectionMaterialDto | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [previewMat, setPreviewMat] = useState<SectionMaterialDto | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErr, setPreviewErr] = useState<string | null>(null);

  const reloadMaterials = async () => {
    if (!currentTenantId || !effectiveSectionId) {
      setMaterials([]);
      return;
    }
    setLoadingMaterials(true);
    setError(null);
    try {
      const res = await courseMaterialApi.list({
        tenantId: currentTenantId,
        sectionId: effectiveSectionId,
      });
      setMaterials(res.items);
    } catch (e: unknown) {
      setError(errorText(e, 'Error al cargar materiales'));
    } finally {
      setLoadingMaterials(false);
    }
  };

  useEffect(() => {
    void reloadMaterials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenantId, effectiveSectionId]);

  const grouped = useMemo(() => {
    const g: Record<MaterialType, SectionMaterialDto[]> = { video: [], notes: [], reading: [] };
    // In the embedded Biblioteca, hide materials already assigned to a module —
    // it's a pool of unassigned materials only.
    for (const m of materials) {
      if (hidden.has(m.id)) continue;
      g[m.type].push(m);
    }
    return g;
  }, [materials, hidden]);

  const handleDelete = async (id: string) => {
    if (!currentTenantId) return;
    try {
      setDeletingId(id);
      await courseMaterialApi.delete({ tenantId: currentTenantId, materialId: id });
      await reloadMaterials();
    } catch (e: unknown) {
      setError(errorText(e, 'Error al eliminar'));
    } finally {
      setDeletingId(null);
    }
  };

  const handlePreview = async (m: SectionMaterialDto) => {
    setPreviewMat(m);
    setPreviewErr(null);
    // External links (video/zoom/youtube) preview embedded in the modal.
    if (m.url) {
      setPreviewUrl(m.url);
      setPreviewLoading(false);
      return;
    }
    if (!m.storagePath || !currentTenantId) return;
    setPreviewUrl(null);
    setPreviewLoading(true);
    try {
      const { url } = await courseMaterialApi.getDownloadUrl({
        tenantId: currentTenantId,
        materialId: m.id,
      });
      setPreviewUrl(url);
    } catch (e) {
      setPreviewErr(errorText(e, 'No se pudo abrir el material.'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const actionButtons = (
    <Stack direction="row" spacing={1} flexWrap="wrap">
      <Button
        variant="contained"
        startIcon={<UploadFileRounded />}
        disabled={!effectiveSectionId}
        onClick={() => setUploadOpen(true)}
      >
        {t('courseMaterials.actions.upload', { ns: 'sis', defaultValue: 'Subir archivo' })}
      </Button>
      <Button
        variant="outlined"
        startIcon={<LinkRounded />}
        disabled={!effectiveSectionId}
        onClick={() => setLinkOpen(true)}
      >
        {t('courseMaterials.actions.addLink', { ns: 'sis', defaultValue: 'Agregar enlace' })}
      </Button>
      <Button
        variant="outlined"
        startIcon={<PlaylistAddRounded />}
        disabled={!effectiveSectionId}
        onClick={() => setBulkOpen(true)}
      >
        {t('courseMaterials.actions.bulk', { ns: 'sis', defaultValue: 'Importar varios' })}
      </Button>
    </Stack>
  );

  return (
    <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
      {!embedded && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {t('courseMaterials.title', { ns: 'sis', defaultValue: 'Material de Clase' })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('courseMaterials.subtitle', {
                ns: 'sis',
                defaultValue:
                  'Publica videos, apuntes y lecturas por sección. Solo alumnos inscritos accederán al material.',
              })}
            </Typography>
          </Box>
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {embedded ? (
        <Box sx={{ mb: 2 }}>{actionButtons}</Box>
      ) : (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <SectionPicker value={selectedSectionId} onChange={setSelectedSectionId} />
            {actionButtons}
          </Stack>
        </Paper>
      )}

      {!embedded && !selectedSectionId && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary">
            Selecciona una sección para gestionar su material.
          </Typography>
        </Paper>
      )}

      {effectiveSectionId && !loadingMaterials && materials.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary">
            {t('courseMaterials.emptyLibrary', {
              ns: 'sis',
              defaultValue:
                'Aún no hay material en esta sección. Sube un archivo o agrega un enlace.',
            })}
          </Typography>
        </Paper>
      )}

      {effectiveSectionId && loadingMaterials && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2 }}>
          <CircularProgress size={20} />
          <Typography color="text.secondary">Cargando material…</Typography>
        </Stack>
      )}

      {effectiveSectionId && !loadingMaterials && materials.length > 0 && (
        <Stack spacing={2}>
          {(Object.keys(grouped) as MaterialType[]).map((type) => {
            const list = grouped[type];
            if (list.length === 0) return null;
            const meta = TYPE_META[type];
            return (
              <Paper key={type} variant="outlined" sx={{ borderRadius: 2 }}>
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderBottom: '1px solid rgba(255,255,255,0.07)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box sx={{ color: meta.color, display: 'flex' }}>{meta.icon}</Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {meta.label} ({list.length})
                  </Typography>
                </Box>
                {list.map((m) => (
                  <Box
                    key={m.id}
                    sx={{
                      px: 2,
                      py: 1.25,
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      gap: 2,
                      borderTop: '1px solid rgba(255,255,255,0.05)',
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {m.title}
                      </Typography>
                      {m.description && (
                        <Typography variant="caption" color="text.secondary">
                          {m.description}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={1} mt={0.5} alignItems="center">
                        {m.url && (
                          <Chip
                            size="small"
                            icon={<OpenInNewRounded sx={{ fontSize: 14 }} />}
                            label={m.externalSource ?? 'enlace'}
                            sx={{ height: 20 }}
                          />
                        )}
                        {m.storagePath && m.originalFileName && (
                          <Chip
                            size="small"
                            label={m.originalFileName}
                            sx={{ height: 20, maxWidth: 240 }}
                          />
                        )}
                        {m.sizeBytes !== undefined && (
                          <Typography variant="caption" color="text.secondary">
                            {(m.sizeBytes / 1024 / 1024).toFixed(1)} MB
                          </Typography>
                        )}
                      </Stack>
                      {showAssign && (
                        <Box sx={{ mt: 1 }}>
                          <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel>
                              {t('courseMaterials.assign.add', {
                                ns: 'sis',
                                defaultValue: 'Agregar a módulo',
                              })}
                            </InputLabel>
                            <Select
                              label={t('courseMaterials.assign.add', {
                                ns: 'sis',
                                defaultValue: 'Agregar a módulo',
                              })}
                              value=""
                              disabled={assigningId === m.id}
                              onChange={async (e) => {
                                const moduleId = e.target.value;
                                if (!moduleId) return;
                                setAssigningId(m.id);
                                try {
                                  await onAssignToModule!(m.id, moduleId);
                                } finally {
                                  setAssigningId(null);
                                }
                              }}
                            >
                              {moduleOptions!.map((mod) => (
                                <MenuItem key={mod.id} value={mod.id}>
                                  {mod.title}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {assigningId === m.id && (
                            <CircularProgress size={14} sx={{ ml: 1, verticalAlign: 'middle' }} />
                          )}
                        </Box>
                      )}
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      {(m.storagePath || m.url) && (
                        <Tooltip title="Previsualizar">
                          <IconButton size="small" onClick={() => handlePreview(m)}>
                            <VisibilityRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => setEditing(m)}>
                          <EditNoteRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton
                          size="small"
                          color="error"
                          disabled={deletingId === m.id}
                          onClick={() => handleDelete(m.id)}
                        >
                          {deletingId === m.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteOutlineRounded fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                ))}
              </Paper>
            );
          })}
          {materials.length === 0 && (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
              <Typography color="text.secondary">
                No hay material publicado en esta sección.
              </Typography>
            </Paper>
          )}
        </Stack>
      )}

      {effectiveSectionId && (
        <>
          <UploadMaterialDialog
            open={uploadOpen}
            onClose={() => setUploadOpen(false)}
            onSuccess={async (_ids) => {
              setUploadOpen(false);
              await reloadMaterials();
            }}
            sectionId={effectiveSectionId}
          />
          <LinkMaterialDialog
            open={linkOpen}
            onClose={() => setLinkOpen(false)}
            onSuccess={async (_ids) => {
              setLinkOpen(false);
              await reloadMaterials();
            }}
            sectionId={effectiveSectionId}
          />
          <BulkLinkMaterialDialog
            open={bulkOpen}
            onClose={() => setBulkOpen(false)}
            onSuccess={async (_ids) => {
              setBulkOpen(false);
              await reloadMaterials();
            }}
            sectionId={effectiveSectionId}
          />
          <EditMaterialDialog
            open={!!editing}
            material={editing}
            onClose={() => setEditing(null)}
            onSuccess={async () => {
              setEditing(null);
              await reloadMaterials();
            }}
          />
        </>
      )}

      <Dialog
        open={!!previewMat}
        onClose={() => setPreviewMat(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '80vh' } }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }} noWrap>
              {previewMat?.title}
            </Typography>
            {previewUrl && (
              <Tooltip title="Abrir en pestaña nueva">
                <IconButton
                  size="small"
                  onClick={() => window.open(previewUrl, '_blank', 'noopener,noreferrer')}
                >
                  <OpenInNewRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={() => setPreviewMat(null)}>
              <CloseRounded fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {previewLoading ? (
            <Box textAlign="center" py={8}>
              <CircularProgress />
            </Box>
          ) : previewErr ? (
            <Alert severity="error">{previewErr}</Alert>
          ) : previewUrl && previewMat ? (
            <PreviewBody material={previewMat} url={previewUrl} />
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

// ─── Edit dialog (metadata only) ───────────────────────────────────────────

function EditMaterialDialog({
  open,
  material,
  onClose,
  onSuccess,
}: {
  open: boolean;
  material: SectionMaterialDto | null;
  onClose: () => void;
  onSuccess: () => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<MaterialType>('notes');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (material) {
      setTitle(material.title);
      setDescription(material.description ?? '');
      setType(material.type);
      setErr(null);
    }
  }, [material]);

  // A file-backed material cannot become a `video` (videos must be URLs).
  const isFile = !!material?.storagePath;
  const typeOptions: MaterialType[] = isFile ? ['notes', 'reading'] : ['video', 'notes', 'reading'];

  const handleSubmit = async () => {
    if (!currentTenantId || !material) return;
    if (!title.trim()) {
      setErr(t('courseMaterials.editDialog.titleRequired'));
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      await courseMaterialApi.update({
        tenantId: currentTenantId,
        materialId: material.id,
        title: title.trim(),
        description: description.trim(),
        type,
      });
      onSuccess();
    } catch (e: unknown) {
      setErr(errorText(e, 'Error al guardar'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!material) {
    return (
      <Dialog open={false} onClose={onClose}>
        <DialogContent />
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('courseMaterials.editDialog.titleWithType', {
          type: t(`courseMaterials.types.${material.type}`),
        })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {err && <Alert severity="error">{err}</Alert>}
          <TextField
            label={t('courseMaterials.editDialog.fieldTitle')}
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ maxLength: 200 }}
          />
          <FormControl size="small" fullWidth>
            <InputLabel id="edit-material-type-label">
              {t('courseMaterials.editDialog.fieldType')}
            </InputLabel>
            <Select
              labelId="edit-material-type-label"
              label={t('courseMaterials.editDialog.fieldType')}
              value={type}
              onChange={(e) => setType(e.target.value as MaterialType)}
            >
              {typeOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {t(`courseMaterials.types.${opt}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('courseMaterials.editDialog.fieldDescription')}
            size="small"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            inputProps={{ maxLength: 2000 }}
          />
          {isFile && (
            <Typography variant="caption" color="text.secondary">
              {t('courseMaterials.editDialog.videoNeedsUrl')}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('courseMaterials.editDialog.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          {submitting
            ? t('courseMaterials.editDialog.saving')
            : t('courseMaterials.editDialog.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
